import converter from '../../utils/converter';
import helpers from '../../utils/helpers';
import errorService from '../errors/errorService';
import NoDeviceIsSelectedError from '../errors/noDeviceIsSelectedError';
import log from '../logger/log';
import {DEFAULT_REPORT_COUNT, EXTENDED_FRAME_SIZE, REPORT_ID_63, REPORT_ID_64} from '../scp/scpConsts';

let instance: HIDClient | null = null;
const VENDOR_ID = 0x2759;

interface IHidClientOptions {
    onDeviceConnect: () => void;
    onDisconnected: (isDisconnectedByUser?: boolean) => Promise<void>;
}

interface IConnectDeviceProps {
    isNewDevice: boolean;
    onConnect: () => void;
}

interface ISubscribeOnInputReportProps {
    onResponse: (response: string, reportIdHex: string) => void;
}

interface ISendReportProps {
    command: string;
    reportId: number;
    isDebug: boolean;
    onError?: () => void;
}

export default class HIDClient {
    private options?: IHidClientOptions;
    private device?: HIDDevice;
    private reportCount?: number;

    constructor(createNew: boolean | undefined = false, options?: IHidClientOptions) {
        if (createNew && instance) {
            instance = null;
        }

        if (instance) {
            return instance;
        }

        this.options = options;

        instance = this;
    }

    connectDevice = async ({isNewDevice, onConnect}: IConnectDeviceProps): Promise<void> => {
        const device = await this.getOpenedDevice(isNewDevice);

        //TODO: check connect event
        navigator.hid.ondisconnect = () => {
            this.onDisconnected();
            log.info(`HID disconnected: ${device.productName}`);
            this.device = undefined;
        };

        this.device = device;
        this.setReportSize();

        log.info(`HIDClient: device is connected, product name: ${device.productName}`);

        this.options?.onDeviceConnect();
        onConnect();
    };

    subscribeOnInputReport = ({onResponse}: ISubscribeOnInputReportProps): void => {
        if (!this.device) return;

        this.device.oninputreport = (e) => {
            const KEEP_ALIVE_FRAME = '0100';
            const {data, reportId} = e;

            const response = converter.buffer2hex(data);
            const reportIdHex = converter.dec2hex(reportId);
            const isNotKeepAliveFrame = !response.startsWith(KEEP_ALIVE_FRAME);

            if (isNotKeepAliveFrame) {
                onResponse(response, reportIdHex);
            }
        };
    };

    sendReport = ({command, isDebug, onError, reportId}: ISendReportProps): void => {
        try {
            const reportSize = this.getReportSize(reportId);
            const commandBin = converter.hex2binSized(command, reportSize);
            const reportIdHex = converter.dec2hex(reportId);

            if (isDebug) {
                log.debug(`Try to send command: ${reportIdHex}${command}`);
            }

            if (!this.device) {
                helpers.runFunction(onError);
                return;
            }

            this.device
                .sendReport(reportId, commandBin)
                .then(() => {
                    if (isDebug) {
                        log.debug(`HIDClient: sendReport success: ${reportIdHex}${command}`);
                    }
                })
                .catch((e) => {
                    helpers.runFunction(onError);
                    log.info(`HIDClient: sendReport failed, error: ${errorService.getErrorMessage(e)}`);
                });
        } catch (e) {
            log.info(`HIDClient: sendReport error: ${errorService.getErrorMessage(e)}`);
        }
    };

    disconnect = (): void => {
        if (!this.device?.opened) return;

        log.info(`HIDClient: disconnect device: ${this.device.productName}`);
        this.device.close();
        this.device = undefined;
        navigator.hid.ondisconnect = null;
        // IA: onDisconnected is not called after device.close()
        // so call it directly
        this.options?.onDisconnected(true);
    };

    isDeviceConnected = (): boolean => !!this.device?.opened;

    getReportSize = (reportId: number): number => {
        switch (reportId) {
            case REPORT_ID_63:
                return this.reportCount || DEFAULT_REPORT_COUNT;

            case REPORT_ID_64:
            default:
                return EXTENDED_FRAME_SIZE - 1;
        }
    };

    private onDisconnected = (isDisconnectedByUser?: boolean): void => {
        log.debug(`HIDClient: device is disconnected`);
        this.options?.onDisconnected(isDisconnectedByUser);
    };

    private getOpenedDevice = async (isNewDevice: boolean): Promise<HIDDevice> => {
        let device;

        if (!isNewDevice) {
            device = await this.getPermittedDevice();
        }

        if (!device) {
            device = await this.requestDevice();
        }

        if (device && !device.opened) {
            await device.open();
        }

        if (!device || !device.opened) {
            throw new NoDeviceIsSelectedError();
        }

        return device;
    };

    private getPermittedDevice = async (): Promise<HIDDevice | undefined> => {
        const devices = await navigator.hid.getDevices();

        return devices.find((d) => d.vendorId === VENDOR_ID);
    };

    private requestDevice = async (): Promise<HIDDevice> => {
        const devices = await navigator.hid.requestDevice({
            filters: [{vendorId: VENDOR_ID}],
        });

        return devices[0];
    };

    private setReportSize = (): void => {
        try {
            const report63 = this.device!.collections[0]!.inputReports!.find(
                (report: HIDReportInfo) => report.reportId === REPORT_ID_63
            );
            const {reportCount} = report63?.items![0]!;

            log.debug(`Set reportCount: ${reportCount}`);
            this.reportCount = reportCount;
        } catch (e) {
            log.debug(`Can't read device reportCount, set default: ${DEFAULT_REPORT_COUNT} reportCount`);
            this.reportCount = DEFAULT_REPORT_COUNT;
        }
    };
}
